import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  DollarSign, 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  TrendingDown, 
  TrendingUp, 
  Clock,
  AlertTriangle,
  Send,
  Plus,
  Lock,
  User,
  Calendar,
  Truck,
  MapPin,
  CreditCard,
  Wifi,
  WifiOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNegotiationSocket } from '../hooks/useNegotiationSocket';
import { logger } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status badge colors
const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-800',
  pending_internal: 'bg-yellow-100 text-yellow-800',
  countered: 'bg-purple-100 text-purple-800',
  pending_client: 'bg-orange-100 text-orange-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-800',
  escalated: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

// Type icons
const TYPE_ICONS = {
  price_change: DollarSign,
  schedule_change: Calendar,
  payment_terms: CreditCard,
  truck_type_change: Truck,
  route_change: MapPin,
  cancel_or_pause: XCircle,
  other: MessageSquare,
};

const NegotiationPanelV2 = ({ operation, onUpdate }) => {
  const { user } = useAuth();
  const [negotiations, setNegotiations] = useState([]);
  const [currentNegotiation, setCurrentNegotiation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [showNewNegotiationDialog, setShowNewNegotiationDialog] = useState(false);
  const [showCounterOfferDialog, setShowCounterOfferDialog] = useState(false);
  const [negotiationType, setNegotiationType] = useState('price_change');
  const [initialMessage, setInitialMessage] = useState('');
  const [proposedPrice, setProposedPrice] = useState('');
  const [counterMessage, setCounterMessage] = useState('');
  const [counterPrice, setCounterPrice] = useState('');
  const [newMessage, setNewMessage] = useState('');

  // Negotiation types from API
  const [negotiationTypes, setNegotiationTypes] = useState([]);

  const isInternalUser = user?.role && ['admin', 'operateur', 'commercial', 'field_ops'].includes(user.role);
  const isClient = user?.role && ['client', 'expediteur'].includes(user.role);

  // Fetch negotiation types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await axios.get(`${API}/negotiations/types`, {});
        setNegotiationTypes(response.data.types || []);
      } catch (error) {
        console.error('Error fetching negotiation types:', error);
      }
    };
    fetchTypes();
  }, []);

  // Fetch negotiations for this operation
  const fetchNegotiations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/negotiations?operation_id=${operation.id}`, {});
      setNegotiations(response.data.negotiations || []);
      
      // Select the first active negotiation
      const active = response.data.negotiations?.find(n => 
        !['accepted', 'rejected', 'expired', 'cancelled'].includes(n.status)
      );
      if (active) {
        setCurrentNegotiation(active);
        fetchMessages(active.id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching negotiations:', error);
      setLoading(false);
    }
  }, [operation.id]);

  // Fetch messages for a negotiation
  const fetchMessages = async (negotiationId) => {
    try {
      const response = await axios.get(`${API}/negotiations/${negotiationId}/messages`, {});
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useEffect(() => {
    fetchNegotiations();
    // Auto-refresh every 30 seconds (fallback si WebSocket non connecté)
    const interval = setInterval(fetchNegotiations, 30000);
    return () => clearInterval(interval);
  }, [fetchNegotiations]);

  // ============== WEBSOCKET TEMPS RÉEL ==============
  const handleNegotiationEvent = useCallback((event) => {
    // Si l'événement concerne cette opération, rafraîchir les données
    if (event.operation_id === operation.id) {
      logger.debug('[NegotiationPanelV2] WebSocket event received:', event.event_type);
      fetchNegotiations();
      
      // Si on a une négociation courante et que l'événement la concerne
      if (currentNegotiation && event.negotiation_id === currentNegotiation.id) {
        fetchMessages(currentNegotiation.id);
      }
    }
  }, [operation.id, currentNegotiation, fetchNegotiations]);

  const { isConnected: wsConnected } = useNegotiationSocket({
    onEvent: handleNegotiationEvent,
    showToasts: false // On gère les toasts nous-mêmes dans le contexte
  });

  // Create new negotiation
  const handleCreateNegotiation = async (e) => {
    e.preventDefault();
    if (!initialMessage.trim()) {
      toast({ title: "Erreur", description: "Veuillez entrer un message", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        operationId: operation.id,
        negotiationType: negotiationType,
        initialMessage: initialMessage,
        offerPayload: proposedPrice ? {
          proposedPrice: parseFloat(proposedPrice),
          previousPrice: operation.estimatedPrice || operation.finalPrice
        } : null
      };

      await axios.post(`${API}/negotiations`, payload, {});

      toast({
        title: "✅ Négociation créée",
        description: "Votre demande a été envoyée à l'équipe"
      });

      setShowNewNegotiationDialog(false);
      setInitialMessage('');
      setProposedPrice('');
      fetchNegotiations();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error creating negotiation:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer la négociation",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Perform action on negotiation
  const handleAction = async (action, message = null, offerPayload = null) => {
    if (!currentNegotiation) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/negotiations/${currentNegotiation.id}/actions`, {
        action,
        message,
        offerPayload
      }, {});

      const actionLabels = {
        accept: "Négociation acceptée",
        reject: "Négociation refusée",
        counter: "Contre-proposition envoyée",
        escalate: "Négociation escaladée"
      };

      toast({
        title: `✅ ${actionLabels[action] || 'Action effectuée'}`,
        description: "La négociation a été mise à jour"
      });

      setShowCounterOfferDialog(false);
      setCounterMessage('');
      setCounterPrice('');
      fetchNegotiations();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error performing action:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'effectuer l'action",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Add message
  const handleAddMessage = async (e) => {
    e.preventDefault();
    if (!currentNegotiation || !newMessage.trim()) return;

    try {
      await axios.post(`${API}/negotiations/${currentNegotiation.id}/messages`, {
        message: newMessage
      }, {});

      setNewMessage('');
      fetchMessages(currentNegotiation.id);
    } catch (error) {
      console.error('Error adding message:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer le message",
        variant: "destructive"
      });
    }
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if negotiation is terminal
  const isTerminal = currentNegotiation && 
    ['accepted', 'rejected', 'expired', 'cancelled'].includes(currentNegotiation.status);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with action button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Négociations
          {negotiations.length > 0 && (
            <Badge variant="secondary">{negotiations.length}</Badge>
          )}
        </h3>
        {isClient && !currentNegotiation && (
          <Button onClick={() => setShowNewNegotiationDialog(true)} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Nouvelle négociation
          </Button>
        )}
      </div>

      {/* Negotiation selector when multiple negotiations exist */}
      {negotiations.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {negotiations.map((nego) => (
            <Button
              key={nego.id}
              variant={currentNegotiation?.id === nego.id ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setCurrentNegotiation(nego);
                fetchMessages(nego.id);
              }}
              className={`text-xs ${
                ['accepted', 'rejected', 'expired', 'cancelled'].includes(nego.status)
                  ? 'opacity-60'
                  : ''
              }`}
            >
              {nego.type_label || nego.negotiationType}
              <Badge 
                variant="outline" 
                className={`ml-1 text-xs ${STATUS_COLORS[nego.status] || ''}`}
              >
                {nego.status_label || nego.status}
              </Badge>
            </Button>
          ))}
        </div>
      )}

      {/* No negotiations yet */}
      {negotiations.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Aucune négociation en cours</p>
            <Button 
              onClick={() => setShowNewNegotiationDialog(true)} 
              className="mt-4"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-1" />
              {isClient ? 'Démarrer une négociation' : 'Initier une négociation'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Current negotiation */}
      {currentNegotiation && (
        <Card className={currentNegotiation.isOverdue ? 'border-red-300' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {(() => {
                  const Icon = TYPE_ICONS[currentNegotiation.negotiationType] || MessageSquare;
                  return <Icon className="h-5 w-5 text-gray-500" />;
                })()}
                <CardTitle className="text-base">
                  {currentNegotiation.type_label || currentNegotiation.negotiationType}
                </CardTitle>
                {/* Indicateur WebSocket temps réel */}
                <div className="flex items-center gap-1" title={wsConnected ? "Temps réel actif" : "Mode hors-ligne"}>
                  {wsConnected ? (
                    <Wifi className="h-3 w-3 text-green-500" />
                  ) : (
                    <WifiOff className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {currentNegotiation.isLocked && (
                  <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                    <Lock className="h-3 w-3 mr-1" />
                    Verrouillée
                  </Badge>
                )}
                {currentNegotiation.isOverdue && (
                  <Badge variant="destructive">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    SLA dépassé
                  </Badge>
                )}
                <Badge className={STATUS_COLORS[currentNegotiation.status]}>
                  {currentNegotiation.status_label || currentNegotiation.status}
                </Badge>
              </div>
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Créée par {currentNegotiation.createdByName} • {formatDate(currentNegotiation.createdAt)}
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Current offer summary */}
            {currentNegotiation.currentOffer && (
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm font-medium text-blue-800 mb-2">Dernière proposition</div>
                <div className="flex items-center justify-between">
                  <div>
                    {currentNegotiation.currentOffer.previousPrice && (
                      <span className="text-gray-500 line-through mr-2">
                        {formatPrice(currentNegotiation.currentOffer.previousPrice)}
                      </span>
                    )}
                    <span className="text-xl font-bold text-blue-700">
                      {formatPrice(currentNegotiation.currentOffer.proposedPrice)}
                    </span>
                  </div>
                  {currentNegotiation.currentOffer.previousPrice && currentNegotiation.currentOffer.proposedPrice && (
                    <Badge variant="outline" className={
                      currentNegotiation.currentOffer.proposedPrice < currentNegotiation.currentOffer.previousPrice
                        ? 'text-green-600 border-green-300'
                        : 'text-red-600 border-red-300'
                    }>
                      {currentNegotiation.currentOffer.proposedPrice < currentNegotiation.currentOffer.previousPrice ? (
                        <><TrendingDown className="h-3 w-3 mr-1" />
                        -{Math.round((1 - currentNegotiation.currentOffer.proposedPrice / currentNegotiation.currentOffer.previousPrice) * 100)}%</>
                      ) : (
                        <><TrendingUp className="h-3 w-3 mr-1" />
                        +{Math.round((currentNegotiation.currentOffer.proposedPrice / currentNegotiation.currentOffer.previousPrice - 1) * 100)}%</>
                      )}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* SLA countdown */}
            {currentNegotiation.slaDeadlineAt && !isTerminal && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                <span>
                  Réponse attendue avant {formatDate(currentNegotiation.slaDeadlineAt)}
                </span>
              </div>
            )}

            <Separator />

            {/* Messages thread */}
            <div className="space-y-2">
              <div className="font-medium text-sm">Fil de discussion</div>
              <ScrollArea className="h-64 rounded border p-3">
                {messages.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">Aucun message</p>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-3 rounded-lg ${
                          msg.authorId === user?.id
                            ? 'bg-blue-100 ml-8'
                            : 'bg-gray-100 mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {msg.authorName}
                            <Badge variant="outline" className="text-xs ml-1">
                              {msg.authorRole}
                            </Badge>
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(msg.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm">{msg.message}</p>
                        {msg.isOffer && msg.offerPayload?.proposedPrice && (
                          <div className="mt-2 p-2 bg-white rounded border">
                            <span className="text-sm font-medium">
                              💰 Offre: {formatPrice(msg.offerPayload.proposedPrice)}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Add message form */}
            {!isTerminal && (
              <form onSubmit={handleAddMessage} className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  className="flex-1"
                />
                <Button type="submit" size="sm" disabled={!newMessage.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            )}

            <Separator />

            {/* Action buttons */}
            {!isTerminal && (
              <div className="flex flex-wrap gap-2">
                {/* Debug info - à supprimer après fix */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="w-full text-xs text-gray-400 mb-2">
                    Debug: isClient={String(isClient)}, isInternalUser={String(isInternalUser)}, status={currentNegotiation.status}, role={user?.role}
                  </div>
                )}
                
                {/* Client actions - can respond when status is 'countered' or 'pending_client' */}
                {isClient && ['countered', 'pending_client'].includes(currentNegotiation.status) && (
                  <>
                    <Button
                      onClick={() => handleAction('accept', 'J\'accepte cette proposition')}
                      disabled={submitting}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Accepter
                    </Button>
                    <Button
                      onClick={() => setShowCounterOfferDialog(true)}
                      disabled={submitting}
                      variant="outline"
                    >
                      <TrendingDown className="h-4 w-4 mr-1" />
                      Contre-proposer
                    </Button>
                    <Button
                      onClick={() => handleAction('reject', 'Je refuse cette proposition')}
                      disabled={submitting}
                      variant="destructive"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Refuser
                    </Button>
                  </>
                )}

                {/* Message when client is waiting for admin response */}
                {isClient && ['open', 'pending_internal', 'escalated'].includes(currentNegotiation.status) && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-800 rounded-lg w-full">
                    <Clock className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">
                      {currentNegotiation.status === 'escalated' 
                        ? "Votre demande a été escaladée et est en cours d'examen par un responsable."
                        : "Votre proposition est en cours d'examen par notre équipe. Vous serez notifié dès qu'une réponse sera disponible."
                      }
                    </span>
                  </div>
                )}

                {/* Internal user actions - can respond when status is 'open', 'pending_internal', or 'escalated' */}
                {isInternalUser && ['open', 'pending_internal', 'escalated'].includes(currentNegotiation.status) && (
                  <>
                    <Button
                      onClick={() => setShowCounterOfferDialog(true)}
                      disabled={submitting}
                      variant="outline"
                    >
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Faire une proposition
                    </Button>
                    <Button
                      onClick={() => handleAction('accept', 'Proposition acceptée')}
                      disabled={submitting}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Accepter
                    </Button>
                    <Button
                      onClick={() => handleAction('reject', 'Proposition refusée')}
                      disabled={submitting}
                      variant="destructive"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Refuser
                    </Button>
                    {currentNegotiation.status !== 'escalated' && (
                      <Button
                        onClick={() => handleAction('escalate', 'Escalade nécessaire')}
                        disabled={submitting}
                        variant="outline"
                        className="text-orange-600 border-orange-300"
                      >
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        Escalader
                      </Button>
                    )}
                  </>
                )}

                {/* Message when waiting for client response (countered status) */}
                {isInternalUser && currentNegotiation.status === 'countered' && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 text-blue-800 rounded-lg">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm">En attente de la réponse du client à votre contre-proposition</span>
                  </div>
                )}
              </div>
            )}

            {/* Terminal state message */}
            {isTerminal && (
              <div className={`p-4 rounded-lg text-center ${
                currentNegotiation.status === 'accepted' ? 'bg-green-50 text-green-800' :
                currentNegotiation.status === 'rejected' ? 'bg-red-50 text-red-800' :
                'bg-gray-50 text-gray-800'
              }`}>
                {currentNegotiation.status === 'accepted' && (
                  <><CheckCircle className="h-5 w-5 mx-auto mb-2" />Négociation acceptée</>
                )}
                {currentNegotiation.status === 'rejected' && (
                  <><XCircle className="h-5 w-5 mx-auto mb-2" />Négociation refusée</>
                )}
                {currentNegotiation.status === 'expired' && (
                  <><Clock className="h-5 w-5 mx-auto mb-2" />Négociation expirée</>
                )}
                {currentNegotiation.status === 'cancelled' && (
                  <><XCircle className="h-5 w-5 mx-auto mb-2" />Négociation annulée</>
                )}
                {currentNegotiation.resolutionNote && (
                  <p className="text-sm mt-2">{currentNegotiation.resolutionNote}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* New Negotiation Dialog */}
      <Dialog open={showNewNegotiationDialog} onOpenChange={setShowNewNegotiationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle négociation</DialogTitle>
            <DialogDescription>
              Créez une demande de négociation pour cette opération
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateNegotiation} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Type de négociation</label>
              <Select value={negotiationType} onValueChange={setNegotiationType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {negotiationTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {negotiationType === 'price_change' && (
              <div>
                <label className="text-sm font-medium">Prix proposé (FCFA)</label>
                <Input
                  type="number"
                  value={proposedPrice}
                  onChange={(e) => setProposedPrice(e.target.value)}
                  placeholder="Ex: 1500000"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Prix actuel: {formatPrice(operation.estimatedPrice || operation.finalPrice)}
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Message</label>
              <Textarea
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                placeholder="Expliquez votre demande..."
                rows={3}
                className="mt-1"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewNegotiationDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Envoi...' : 'Envoyer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Counter Offer Dialog */}
      <Dialog open={showCounterOfferDialog} onOpenChange={setShowCounterOfferDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isClient ? 'Contre-proposition' : 'Faire une proposition'}
            </DialogTitle>
            <DialogDescription>
              Proposez un nouveau tarif pour cette négociation
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            handleAction('counter', counterMessage, {
              proposedPrice: parseFloat(counterPrice),
              previousPrice: currentNegotiation?.currentOffer?.proposedPrice
            });
          }} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nouveau tarif proposé (FCFA)</label>
              <Input
                type="number"
                value={counterPrice}
                onChange={(e) => setCounterPrice(e.target.value)}
                placeholder="Ex: 1750000"
                className="mt-1"
                required
              />
              {currentNegotiation?.currentOffer?.proposedPrice && (
                <p className="text-xs text-gray-500 mt-1">
                  Dernière offre: {formatPrice(currentNegotiation.currentOffer.proposedPrice)}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Message (optionnel)</label>
              <Textarea
                value={counterMessage}
                onChange={(e) => setCounterMessage(e.target.value)}
                placeholder="Expliquez votre proposition..."
                rows={3}
                className="mt-1"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCounterOfferDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting || !counterPrice}>
                {submitting ? 'Envoi...' : 'Envoyer la proposition'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NegotiationPanelV2;
